import { filtersState } from '../features/filter/filterSlice';
import { MotionTypeOptions } from '../interfaces';

export const toCapitalize = (phrase: string) => {
  const arr = phrase.split(' ');

  for (let index = 0; index < arr.length; index++) {
    arr[index] = arr[index].charAt(0).toUpperCase() + arr[index].slice(1);
  }

  return arr.join(' ');
};

export const toCapitalizeWord = (name: string): string => {
  return name.charAt(0).toLocaleUpperCase() + name.slice(1).toLocaleLowerCase().replaceAll('_', ' ');
};

export const updateValuesFromUrl = ({ key, value }: { key: keyof typeof filtersState; value: string }) => {
  switch (key) {
    case 'motionType':
    case 'mountingType':
      return { name: key, newValue: value };
    case 'cushioningAdjustments':
    case 'cushioningTechnologies':
      const array = value.split(',');
      return { name: key, newValue: array };
    case 'fixedStop':
    case 'recommendedResults':
      return { name: key, newValue: value === 'true' };
    case 'soft':
    case 'robust':
    case 'forBatteryProduction':
      return { name: key, newValue: value === 'true' ? true : null };
    default:
      return { name: key, newValue: parseFloat(value) };
  }
};

export const preparationSectionTranslationKey = (pathname: string): string | undefined => {
  // Separate the url by / then filter so that there is no empty element and we keep the last element, which corresponds to the motion we are in
  // we clean the string of - and _ and proceed to convert it into a translationKey such that => verticalMotionDownwardsMovingMassWithDrivingForce

  const firstPart = pathname
    .split('/')
    .filter((p) => p !== '')
    .pop()
    ?.replace('_', ' ')
    ?.replaceAll('-', ' ');

  if (!firstPart) return;
  const secondPart = firstPart
    .split(' ')
    .map((p, index) => (index === 0 ? p : toCapitalize(p)))
    .join()
    .replaceAll(',', '');

  return `useCase${toCapitalize(secondPart)}`;
};

export const hashCode = (s: string): number => {
  let h = 0;

  if (s.length > 0) {
    for (let i = 0; i < s.length; i++) {
      h = Math.imul(31, h) + s.charCodeAt(i);
    }
  }

  return h;
};

export const checkFilterValue = (value: string[] | string | number, name: string) => {
  switch (name) {
    // Check if value is an array and return true/false or true/null
    case 'fixedStop':
    case 'recommendedResults':
      if (typeof value === 'string' || typeof value === 'number') return value;
      return value.length > 0;
    case 'forBatteryProduction':
    case 'soft':
    case 'robust':
      if (!Array.isArray(value)) return value;
      return value.length > 0 ? true : null;
    default:
      return value;
  }
};

export const listOfMotionType: { [x: string]: MotionTypeOptions } = {
  verticalmotionfreefallingmass: 'VERTICAL_MOTION_FREE_FALLING_MASS',
  verticalmotiondownwardsmovingmasswithdrivingforce: 'VERTICAL_MOTION_DOWNWARDS_MOVING_MASS_WITH_DRIVING_FORCE',
  verticalmotionupwardsmovingmasswithdrivingforce: 'VERTICAL_MOTION_UPWARDS_MOVING_MASS_WITH_DRIVING_FORCE',
  horizontalmotionmovingmassonrollers: 'HORIZONTAL_MOTION_MOVING_MASS_ON_ROLLERS',
  horizontalmotionmovingmasswithdrivingforce: 'HORIZONTAL_MOTION_MOVING_MASS_WITH_DRIVING_FORCE',
  horizontalmotionmovingmassonbelt: 'HORIZONTAL_MOTION_MOVING_MASS_ON_BELT',
  inclinemotionmovingmassdownwardsoninclinedplane: 'INCLINE_MOTION_MOVING_MASS_DOWNWARDS_ON_INCLINED_PLANE',
  inclinemotionmovingmassdownwardsoninclinedplanewithdrivingforce:
    'INCLINE_MOTION_MOVING_MASS_DOWNWARDS_ON_INCLINED_PLANE_WITH_DRIVING_FORCE',
  inclinemotionmovingmassupwardsoninclinedplanewithdrivingforce:
    'INCLINE_MOTION_MOVING_MASS_UPWARDS_ON_INCLINED_PLANE_WITH_DRIVING_FORCE',
  rotationalmotionfreelymovingleveratverticalimpact: 'ROTATIONAL_MOTION_FREELY_MOVING_LEVER_AT_VERTICAL_IMPACT',
  rotationalmotiondownwardsmovingleverwithdrivingtorqueatverticalimpact:
    'ROTATIONAL_MOTION_DOWNWARDS_MOVING_LEVER_WITH_DRIVING_TORQUE_AT_VERTICAL_IMPACT',
  rotationalmotionupwardsmovingleverwithdrivingtorqueatverticalimpact:
    'ROTATIONAL_MOTION_UPWARDS_MOVING_LEVER_WITH_DRIVING_TORQUE_AT_VERTICAL_IMPACT',
  rotationalmotionupwardsmovingleverwithdrivingtorqueandsideimpact:
    'ROTATIONAL_MOTION_UPWARDS_MOVING_LEVER_WITH_DRIVING_TORQUE_AND_SIDE_IMPACT',
  rotationalmotiondownwardsmovingleverwithdrivingtorqueandsideimpact:
    'ROTATIONAL_MOTION_DOWNWARDS_MOVING_LEVER_WITH_DRIVING_TORQUE_AND_SIDE_IMPACT',
  rotationalmotionfreelymovingleverwithsideimpact: 'ROTATIONAL_MOTION_FREELY_MOVING_LEVER_WITH_SIDE_IMPACT',
  rotationalmotionrotatingleveronhorizontalplanewithdrivingtorque:
    'ROTATIONAL_MOTION_ROTATING_LEVER_ON_HORIZONTAL_PLANE_WITH_DRIVING_TORQUE',
  rotationalmotionrotatingdiskonverticalplane: 'ROTATIONAL_MOTION_ROTATING_DISK_ON_VERTICAL_PLANE',
  rotationalmotionswinginglinkagewithdrivingforce: 'ROTATIONAL_MOTION_SWINGING_LINKAGE_WITH_DRIVING_FORCE',
};

// function isReferrerValid(silentRenewUrl = ''): boolean {
//   if (!silentRenewUrl || !document.referrer) {
//     return false;
//   }
//
//   const referrer = new URL(document.referrer);
//   const issuer = new URL(silentRenewUrl);
//
//   return referrer.host === issuer.host && referrer.pathname.includes('login');
// }

export function isLoginRedirect() {
  const silentRenewUrl = checkFestoDomain(
    `${process.env.REACT_APP_OAUTH_SILENT_RENEW_URL}`,
    `${process.env.REACT_APP_OAUTH_SILENT_RENEW_URL_CHINA}`,
  );
  const idpUrl = process.env.REACT_APP_OAUTH_ISSUER;

  return (
    idpUrl &&
    silentRenewUrl &&
    document.referrer &&
    (() => {
      const idp = new URL(idpUrl);
      const referrer = new URL(document.referrer);
      const issuer = new URL(silentRenewUrl);
      return (referrer.host === issuer.host && referrer.pathname.includes('login')) || referrer.host === idp.host;
    })()
  );
}

export const checkFestoDomain = (defaultDomain: string, alternativeDomain: string) => {
  const currentDomain = window.location.hostname;

  return currentDomain.includes('festo.com.cn') ? alternativeDomain : defaultDomain;
};

export const getSalesOrgMap = (countryCode: string) => {
  const salesOrgMap = new Map<string, string>([
    ['hq', '0001'],
    ['nl', '0100'],
    ['dk', '0110'],
    ['se', '0120'],
    ['es', '0130'],
    ['fr', '0140'],
    ['be', '0150'],
    ['no', '0160'],
    ['za', '0170'],
    ['fi', '0180'],
    ['ie', '0190'],
    ['de', '0002'],
    ['us', '0200'],
    ['ca', '0210'],
    ['br', '0220'],
    ['mx', '0230'],
    ['ar', '0240'],
    ['co', '0260'],
    ['cl', '0280'],
    ['pe', '0290'],
    ['au', '0300'],
    ['nz', '0301'],
    ['jp', '0310'],
    ['id', '0320'],
    ['sg', '0330'],
    ['vn', '0335'],
    ['ph', '0340'],
    ['kr', '0360'],
    ['my', '0370'],
    ['hk', '0380'],
    ['cn', '0400'],
    ['tw', '0420'],
    ['in', '0430'],
    ['th', '0440'],
    ['pl', '0500'],
    ['cz', '0510'],
    ['hu', '0525'],
    ['bg', '0535'],
    ['sk', '0540'],
    ['ro', '0550'],
    ['ua', '0570'],
    ['hr', '0580'],
    ['si', '0590'],
    ['at', '0060'],
    ['tr', '0620'],
    ['gr', '0630'],
    ['lt', '0660'],
    ['lv', '0670'],
    ['il', '0680'],
    ['pt', '0690'],
    ['ch', '0070'],
    ['ae', '0760'],
    ['rs', '0780'],
    ['gb', '0080'],
    ['it', '0090'],
  ]);
  return salesOrgMap.get(countryCode.toLowerCase());
};

export const getCompanyIdFromFWALayer = () => {
  const fwaLayer = window.fwaLayer;
  if (fwaLayer) {
    return fwaLayer.user.companyId;
  }
};

export const getDealerRightsValue = () => {
  const fwaLayer = window.fwaLayer;
  if (fwaLayer) {
    return fwaLayer.user.hasDealerRights;
  }
};
